import { useEffect, useRef, useState } from 'react';
import axiosConn from '../axiosConn';
import { handleRemoveSubscriber, isPlayerAuthorized } from '../utils/galaxyUtils';

export default function useGalaxyMemberAdministration({ galaxy, player, setSubscribers, subscribers, onMembersChanged }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [open, setOpen] = useState(false);
  const [excluded, setExcluded] = useState([]);
  const scope = `${galaxy._id}:${player.token}`;
  const current = useRef(scope);
  const pending = useRef(null);
  current.current = scope;
  const authorized = isPlayerAuthorized(galaxy, player);
  useEffect(() => {
    current.current = scope;
    setOpen(false); setExcluded([]); setError(''); setMessage(''); setBusy(false);
    pending.current = null;
    return () => { current.current = null; };
  }, [scope]);
  const options = { headers: { Authorization: `Bearer ${player.token}` } };
  const run = async (action) => {
    if (!authorized || pending.current === scope) return;
    pending.current = scope; setBusy(true); setError(''); setMessage('');
    try { await action(); }
    catch (err) {
      if (current.current === scope) {
        setExcluded([]);
        setError(err?.response?.data?.message || 'The change could not be confirmed. Refresh and try again.');
      }
    } finally {
      if (current.current === scope) { setBusy(false); pending.current = null; }
    }
  };
  const load = async () => {
    const response = await axiosConn.get(`galaxies/${galaxy._id}/excluded-members`, options);
    if (response.data?.data?.canManage !== true || !Array.isArray(response.data?.data?.players))
      throw new Error('Unconfirmed administration response');
    if (current.current === scope) setExcluded(response.data.data.players);
  };
  const remove = (subscriber) => {
    const self = subscriber._id === player.data.player._id;
    const question = self
      ? `Leave ${galaxy.name}? You can return through its normal admission process.`
      : `Remove ${subscriber.playerId} from ${galaxy.name}? They cannot rejoin until a Galaxy manager restores their access. Their Galaxy roles will also be removed.`;
    if (pending.current === scope || !authorized || !window.confirm(question)) return;
    return run(async () => {
      await handleRemoveSubscriber(galaxy, player, subscriber._id,
        (value) => { if (current.current === scope) setSubscribers(value); }, subscribers);
      if (current.current !== scope) return;
      setMessage(self ? 'You left the Galaxy.' : `${subscriber.playerId} was removed. Rejoining is blocked until restoration.`);
      if (onMembersChanged) {
        try { await onMembersChanged(); }
        catch (_) { if (current.current === scope) setError('Removal succeeded. Refresh to update the member list.'); }
      }
      if (open && !self) {
        try { await load(); }
        catch (_) { if (current.current === scope) setError('Removal succeeded. Reopen Removed members to refresh the list.'); }
      }
    });
  };
  const restore = (person) => {
    if (pending.current === scope || !authorized || !window.confirm(
      `Allow ${person.playerId} to rejoin ${galaxy.name}? Normal admission applies. Membership and previous Galaxy roles will not be restored.`,
    )) return;
    return run(async () => {
      const response = await axiosConn.post(`galaxies/${galaxy._id}/restore/${person._id}`, {}, options);
      if (response.status !== 200 || response.data?.status !== 'success') throw new Error('Unconfirmed restoration');
      if (current.current !== scope) return;
      setExcluded((list) => list.filter((item) => item._id !== person._id));
      setMessage(`${person.playerId} can use normal admission again. Membership and prior roles were not restored.`);
    });
  };
  const toggle = () => {
    if (busy) return;
    if (open) { setOpen(false); setExcluded([]); return; }
    setOpen(true); return run(load);
  };
  return { authorized, busy, error, message, open, excluded, remove, restore, toggle };
}
